'use client';

import { Button, Divider, RadioGroup, RadioGroupItem, Typography } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { pathsMusinsa } from '../../common';
import { BottomButtonWrapper } from '../../common/components/BottomButtonWrapper';
import { useCuratorSnackbar } from '../../common/hooks/useCuratorSnackbar';
import { useProposalDetail } from '../hooks/useProposalDetail';
import { useApproveProposal } from '../hooks/useProposalMutations';
import {
  isMusinsaService,
  trackCouponNameCompleteClickMusinsa,
  trackCouponNameOptionClickMusinsa,
  trackCouponNameScreenViewMusinsa,
} from '../utils/campaignProposalMusinsaAnalytics';

interface CouponSettingNamePageViewProps {
  campaignProposalId: number;
}

export const CouponSettingNamePageView = ({
  campaignProposalId,
}: CouponSettingNamePageViewProps) => {
  const router = useRouter();
  const { service } = useDeviceInfo();
  const snackbar = useCuratorSnackbar();
  const { data: detail, isLoading } = useProposalDetail(campaignProposalId);
  const { mutate: approve } = useApproveProposal();
  const [selectedType, setSelectedType] = useState<string>('FOLLOWER');
  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    if (!isMusinsaService(service)) return;
    trackCouponNameScreenViewMusinsa();
  }, [service, campaignProposalId]);

  const handleComplete = () => {
    if (isClicked) return;
    setIsClicked(true);

    if (isMusinsaService(service)) {
      trackCouponNameCompleteClickMusinsa({ couponNameType: selectedType });
    }

    approve(
      {
        campaignProposalId,
        couponNameType: selectedType as 'FOLLOWER' | 'NICKNAME' | 'BRAND',
      },
      {
        onSuccess: () => {
          // 액션 버튼이 있어 네이티브 토스트 대신 MDS 스낵바 경로 사용 (useCuratorSnackbar)
          snackbar.withTextAction('쿠폰 캠페인 제안을 수락했습니다.', '쿠폰 가이드', () =>
            router.push(pathsMusinsa.campaignCouponGuide()),
          );
          router.push(
            pathsMusinsa.campaignCollaboration({
              query: { type: 'BRANDS_PROPOSAL', status: 'APPROVED' },
            }),
          );
        },
        onError: () => {
          setIsClicked(false);
        },
      },
    );
  };

  if (isLoading || !detail) {
    return null;
  }

  return (
    <div className="flex flex-col flex-grow bg-white pb-[72px]!">
      <div className="pt-6 pb-3 px-4">
        <Typography variant="title18pxMed" colorScheme="black">
          <span className="block">사용하실 쿠폰명 1개를 선택해 주세요.</span>
        </Typography>
      </div>

      {/* 쿠폰 이름 선택 */}
      <RadioGroup className="flex flex-col gap-0">
        {detail.couponNameInfos.map((info, index) => {
          // NOTE: click 영역을 위한 처리
          const isFirst = index === 0;
          const isLast = index === detail.couponNameInfos.length - 1;
          const isMiddle = !isFirst && !isLast;

          return (
            <div
              key={info.type}
              role="button"
              className={clsx(
                'px-[16px]! cursor-pointer space-y-[8px]!',
                isFirst && 'pt-[12px]! pb-[16px]!',
                isLast && 'pt-[8px]! pb-[12px]!',
                isMiddle && 'pt-[8px]! pb-[16px]!',
              )}
              tabIndex={index}
              onClick={() => {
                if (info.type === selectedType) return;
                setSelectedType(info.type);
                if (isMusinsaService(service)) {
                  trackCouponNameOptionClickMusinsa({ couponNameType: info.type });
                }
              }}
            >
              <div className="flex items-center gap-x-[8px]">
                <RadioGroupItem value={info.type} checked={selectedType === info.type} />
                <Typography
                  variant="body13pxReg"
                  colorScheme={selectedType === info.type ? 'black' : 'gray-600'}
                >
                  {info.templateLabel}
                </Typography>
              </div>
              <Typography variant="body13pxReg" colorScheme="gray-600" className="ml-[24px]!">
                {info.personalizedLabel}
              </Typography>
            </div>
          );
        })}
      </RadioGroup>

      <div className="px-[16px]!">
        <Divider variant="line" className="my-[12px]!" />
      </div>
      {/* 안내 텍스트 */}
      <div className="flex flex-col px-[16px]! pt-[12px]!">
        <div className="flex gap-[4px]!">
          <Typography variant="textEtc11pxReg" colorScheme="gray-600">
            {'•'}
          </Typography>
          <Typography variant="textEtc11pxReg" colorScheme="gray-600">
            쿠폰명은 큐레이터 마이페이지에서는 확인할 수 없습니다.
            <br />
            무신사 앱 {'>'} 마이 {'>'} 쿠폰 메뉴에서 쿠폰 키워드를 등록하신 후, 쿠폰명을 확인해
            주세요.
          </Typography>
        </div>
      </div>
      <BottomButtonWrapper className="py-3 fixed! z-20">
        <Button variant="primary" className="w-full" disabled={isClicked} onClick={handleComplete}>
          완료
        </Button>
      </BottomButtonWrapper>
    </div>
  );
};
